import React, { useState, useEffect } from 'react';
import { logementService } from '../services/logementService';
import { getErrorMessage } from '../utils/errorHandler';
import { useAuth } from '../contexts/AuthContext';
import Alert from './Alert';
import GoogleAutocomplete from './common/GoogleAutocomplete';

const LogementForm = ({ logement, onSave, onCancel, isEdit = false }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    adresse: '',
    ville: '',
    code_postal: '',
    pays: 'France',
    coordonnees_gps: '',
    cood_gps_standart: '',
    loyer: '',
    montant_charges: '',
    statut: 'disponible'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (logement && isEdit) {
      setFormData({
        titre: logement.titre || '',
        description: logement.description || '',
        adresse: logement.adresse || '',
        ville: logement.ville || '',
        code_postal: logement.code_postal || '',
        pays: logement.pays || 'France',
        coordonnees_gps: logement.coordonnees_gps || '',
        cood_gps_standart: logement.cood_gps_standart || '',
        loyer: logement.loyer || '',
        montant_charges: logement.montant_charges || '',
        statut: logement.statut || 'disponible'
      });
    } else if (!isEdit) {
      // Réinitialiser le formulaire pour un nouveau logement
      setFormData({
        titre: '',
        description: '',
        adresse: '',
        ville: '',
        code_postal: '',
        pays: 'France',
        coordonnees_gps: '',
        cood_gps_standart: '',
        loyer: '',
        montant_charges: '',
        statut: 'disponible'
      });
    }
  }, [logement, isEdit]);

  // Réinitialiser les erreurs quand le formulaire s'ouvre
  useEffect(() => {
    setErrors({});
    setSubmitError(null);
  }, [logement?.id, isEdit]);

  // Fonction pour gérer la réception des coordonnées GPS
  const handleCoordinatesExtracted = (coordinates) => {
    // Format reçu: "long:X,lat:Y,alt:Z"
    // Convertir vers format standard pour marqueur: "lat,lng"
    try {
      const parts = coordinates.split(',');
      if (parts.length === 3) {
        const longitude = parts[0].replace('long:', '');
        const latitude = parts[1].replace('lat:', '');
        const standardFormat = `${latitude},${longitude}`;

        setFormData(prev => ({
          ...prev,
          coordonnees_gps: coordinates,
          cood_gps_standart: standardFormat
        }));
      }
    } catch (error) {
      console.warn('Erreur lors de la conversion des coordonnées GPS:', error);
    }
  };

  // Fonction pour gérer les composants d'adresse extraits
  const handleAddressComponentsExtracted = (components) => {
    setFormData(prev => ({
      ...prev,
      ville: components.ville || prev.ville,
      code_postal: components.code_postal || prev.code_postal,
      pays: components.pays || prev.pays
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.titre.trim()) {
      newErrors.titre = 'Le titre est requis';
    }

    if (!formData.adresse.trim()) {
      newErrors.adresse = 'L\'adresse est requise';
    }

    if (!formData.ville.trim()) {
      newErrors.ville = 'La ville est requise';
    }

    if (!formData.code_postal.trim()) {
      newErrors.code_postal = 'Le code postal est requis';
    } else if (!/^\d{5}$/.test(formData.code_postal)) {
      newErrors.code_postal = 'Le code postal doit contenir 5 chiffres';
    }

    if (!formData.pays.trim()) {
      newErrors.pays = 'Le pays est requis';
    }

    if (!formData.loyer || formData.loyer <= 0) {
      newErrors.loyer = 'Le loyer doit être supérieur à 0';
    }

    if (formData.montant_charges && formData.montant_charges < 0) {
      newErrors.montant_charges = 'Le montant des charges ne peut pas être négatif';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Nettoyer l'erreur du champ modifié
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset des erreurs précédentes
    setSubmitError(null);
    setErrors({});
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        ...formData,
        loyer: parseFloat(formData.loyer),
        montant_charges: formData.montant_charges ? parseFloat(formData.montant_charges) : 0.0
      };

      let result;
      if (isEdit && logement) {
        result = await logementService.updateLogement(logement.id, dataToSend);
      } else {
        result = await logementService.createLogement(dataToSend, user?.id);
      }

      onSave(result);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setSubmitError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-60 z-50 flex items-center justify-center p-4">
      <div className="relative mx-auto w-full max-w-3xl h-full max-h-[90vh] bg-white shadow-2xl rounded-2xl overflow-hidden flex flex-col">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6">
          <h3 className="text-2xl font-semibold text-white flex items-center">
            <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mr-3">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            {isEdit ? 'Modifier le logement' : 'Nouveau logement'}
          </h3>
          <p className="text-blue-100 mt-1">
            {isEdit ? 'Mettre à jour les informations de ce logement' : 'Ajouter un nouveau logement au parc locatif'}
          </p>
        </div>

        {/* Form content - scrollable */}
        <div className="flex-1 overflow-y-auto p-8">
          
          {/* Alerte d'erreur */}
          {submitError && (
            <Alert
              type="error"
              title="Erreur de sauvegarde"
              message={submitError}
              onClose={() => setSubmitError(null)}
            />
          )}
          
          <form id="logement-form" onSubmit={handleSubmit} className="space-y-8">
            {/* Section 1: Informations générales */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Informations générales
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Titre */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Titre *
                  </label>
                  <input
                    type="text"
                    name="titre"
                    value={formData.titre}
                    onChange={handleChange}
                    className={`w-full border-2 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                      errors.titre ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                    placeholder="Appartement moderne centre-ville"
                    maxLength={200}
                  />
                  {errors.titre && (
                    <p className="text-red-500 text-sm mt-2 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.titre}
                    </p>
                  )}
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className={`w-full border-2 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none ${
                      errors.description ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                    placeholder="Description détaillée du logement..."
                    rows={4}
                    maxLength={2000}
                  />
                  {errors.description && (
                    <p className="text-red-500 text-sm mt-2 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.description}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Section 2: Localisation */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Localisation
              </h4>

              <div className="space-y-6">
                {/* Adresse avec autocomplétion Google */}
                <div>
                  <GoogleAutocomplete
                    id="adresse"
                    name="adresse"
                    label="Adresse complète"
                    placeholder="Rechercher l'adresse du logement..."
                    value={formData.adresse}
                    onChange={handleChange}
                    required={true}
                    type="address"
                    errors={errors}
                    onCoordinatesExtracted={handleCoordinatesExtracted}
                    onAddressComponentsExtracted={handleAddressComponentsExtracted}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Ville (lecture seule) */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Ville *
                    </label>
                    <input
                      type="text"
                      name="ville"
                      value={formData.ville}
                      readOnly
                      className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 cursor-not-allowed"
                      placeholder="Auto-rempli"
                    />
                    {errors.ville && (
                      <p className="text-orange-500 text-sm mt-2 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.ville}
                      </p>
                    )}
                  </div>

                  {/* Code postal (lecture seule) */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Code postal *
                    </label>
                    <input
                      type="text"
                      name="code_postal"
                      value={formData.code_postal}
                      readOnly
                      className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 cursor-not-allowed"
                      placeholder="Auto-rempli"
                    />
                    {errors.code_postal && (
                      <p className="text-orange-500 text-sm mt-2 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.code_postal}
                      </p>
                    )}
                  </div>

                  {/* Pays (lecture seule) */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Pays *
                    </label>
                    <input
                      type="text"
                      name="pays"
                      value={formData.pays}
                      readOnly
                      className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 cursor-not-allowed"
                      placeholder="Auto-rempli"
                    />
                    {errors.pays && (
                      <p className="text-orange-500 text-sm mt-2 flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {errors.pays}
                      </p>
                    )}
                  </div>
                </div>

                {/* Affichage des coordonnées GPS extraites */}
                {formData.coordonnees_gps && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4">
                    <div className="flex items-center mb-2">
                      <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                      <span className="text-sm font-semibold text-blue-800">Coordonnées GPS détectées</span>
                    </div>
                    <p className="text-sm text-blue-700 mb-1">
                      📍 {formData.coordonnees_gps}
                    </p>
                    <p className="text-xs text-blue-600">
                      Format carte: {formData.cood_gps_standart}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Section 3: Informations financières */}
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl p-6 border border-orange-200">
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <svg className="w-5 h-5 text-orange-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
                Informations financières
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Loyer */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Loyer mensuel (€) *
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                      <span className="text-gray-500 font-medium">€</span>
                    </div>
                    <input
                      type="number"
                      name="loyer"
                      value={formData.loyer}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      className={`w-full border-2 rounded-lg pl-8 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${
                        errors.loyer ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                      placeholder="450.00"
                    />
                  </div>
                  {errors.loyer && (
                    <p className="text-red-500 text-sm mt-2 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.loyer}
                    </p>
                  )}
                </div>

                {/* Montant des charges */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Montant des charges (€)
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                      <span className="text-gray-500 font-medium">€</span>
                    </div>
                    <input
                      type="number"
                      name="montant_charges"
                      value={formData.montant_charges}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      className={`w-full border-2 rounded-lg pl-8 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${
                        errors.montant_charges ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                      placeholder="50.00"
                    />
                  </div>
                  {errors.montant_charges && (
                    <p className="text-red-500 text-sm mt-2 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.montant_charges}
                    </p>
                  )}
                </div>

                {/* Montant total calculé */}
                {(formData.loyer || formData.montant_charges) && (
                  <div className="md:col-span-2">
                    <div className="bg-white rounded-lg p-4 border-2 border-orange-300">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-gray-700">Montant total mensuel</span>
                        <span className="text-2xl font-bold text-orange-600">
                          {(parseFloat(formData.loyer || 0) + parseFloat(formData.montant_charges || 0)).toFixed(2)} €
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Loyer + charges
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Section 4: Statut */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Statut du logement
              </h4>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Statut actuel
                </label>
                <select
                  name="statut"
                  value={formData.statut}
                  onChange={handleChange}
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white hover:border-gray-300"
                >
                  <option value="disponible">✅ Disponible</option>
                  <option value="occupe">🏠 Occupé</option>
                  <option value="maintenance">🔧 Maintenance</option>
                </select>
              </div>
            </div>


          </form>
        </div>

        {/* Footer avec boutons d'action - toujours visible */}
        <div className="bg-white border-t border-gray-200 px-8 py-6 rounded-b-2xl">
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-6 py-3 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              <span className="flex items-center justify-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Annuler
              </span>
            </button>
            <button
              type="submit"
              form="logement-form"
              className="flex-1 px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              disabled={loading}
            >
              <span className="flex items-center justify-center">
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isEdit ? "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" : "M12 6v6m0 0v6m0-6h6m-6 0H6"} />
                    </svg>
                    {isEdit ? 'Mettre à jour' : 'Créer le logement'}
                  </>
                )}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogementForm;